import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  listarAgendamentos, buscarAgendamento, criarAgendamento, atualizarAgendamento, deletarAgendamento,
  listarFiliais, listarAreas,
  salvarEnviosAgendamento, listarEnviosAutomaticos
} from '../../../services/montagemPalete';
import { listarClientes } from '../../../services/clientes';
import { buscarConfiguracoesConferencia } from '../../../services/configuracoesConferencia';
import type { MontagemAgendamento, Filial, AreaEntrega, LocalPreCarregamento, TipoPalete } from '../../../types/montagemPalete';
import type { EnvioAutomaticoConfig as EnvioConfig } from '../../../types/config';
import EnvioAutomaticoConfig from '../../../components/MontagemPaletes/EnvioAutomaticoConfig';
import '../MontagemPaletes.css';

interface ClienteOption {
  id: number;
  razao_social: string;
}

const MontagemPaleteAgendamentos: React.FC = () => {
  const navigate = useNavigate();
  const [agendamentos, setAgendamentos] = useState<MontagemAgendamento[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [clientes, setClientes] = useState<ClienteOption[]>([]);
  const [filiais, setFiliais] = useState<Filial[]>([]);
  const [areas, setAreas] = useState<AreaEntrega[]>([]);
  const [erro, setErro] = useState('');
  const [exibirDoca, setExibirDoca] = useState(true);

  const [formData, setFormData] = useState({
    cliente_id: '' as string | number,
    filial_id: '' as string | number,
    local_pre_carregamento: 'cliente' as LocalPreCarregamento,
    intervalo_dias: 1,
    horario: '08:00',
    dias_semana: [] as number[]
  });

  const DIAS_SEMANA_LABELS = [
    { value: 1, label: 'Seg', full: 'Segunda-feira' },
    { value: 2, label: 'Ter', full: 'Terça-feira' },
    { value: 3, label: 'Qua', full: 'Quarta-feira' },
    { value: 4, label: 'Qui', full: 'Quinta-feira' },
    { value: 5, label: 'Sex', full: 'Sexta-feira' },
    { value: 6, label: 'Sáb', full: 'Sábado' },
    { value: 0, label: 'Dom', full: 'Domingo' }
  ];

  const toggleDiaSemana = (dia: number) => {
    setFormData(p => ({
      ...p,
      dias_semana: p.dias_semana.includes(dia)
        ? p.dias_semana.filter(d => d !== dia)
        : [...p.dias_semana, dia]
    }));
  };

  // Areas selecionadas para o modelo do agendamento
  const [areasSelecionadas, setAreasSelecionadas] = useState<{ area_entrega_id: number; doca: string; tipo_palete: TipoPalete }[]>([]);

  // Multi-select mode
  const [modoSelecaoMultipla, setModoSelecaoMultipla] = useState(false);
  const [areasCheckbox, setAreasCheckbox] = useState<number[]>([]);
  const [docaPadrao, setDocaPadrao] = useState('');
  const [tipoPaletePadrao, setTipoPaletePadrao] = useState<TipoPalete>('normal');

  // Single add mode
  const [novaArea, setNovaArea] = useState({ area_entrega_id: '' as string | number, doca: '', tipo_palete: 'normal' as TipoPalete });

  // Envio automático
  const [enviosAutomaticos, setEnviosAutomaticos] = useState<EnvioConfig[]>([]);

  // Callback para quando o usuário troca o cliente manualmente
  const handleClienteChange = (novoClienteId: string) => {
    setFormData(p => ({ ...p, cliente_id: novoClienteId }));
    setAreasCheckbox([]);
    setAreasSelecionadas([]);
  };

  const carregarAgendamentos = useCallback(async () => {
    try {
      setLoading(true);
      const res = await listarAgendamentos();
      if (res.success) setAgendamentos(res.agendamentos);
    } catch (error: any) {
      setErro(error?.message || 'Erro ao carregar agendamentos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregarAgendamentos();

    listarClientes({ limit: 200, ativo: 'true' }).then(res => {
      if (res.success && res.clientes) {
        setClientes(res.clientes.map((c: any) => ({ id: c.id, razao_social: c.razao_social })));
      }
    }).catch((err: any) => setErro(err?.message || 'Erro ao carregar clientes'));

    listarFiliais({ ativo: 'true' }).then(res => {
      if (res.success) setFiliais(res.filiais);
    }).catch((err: any) => setErro(err?.message || 'Erro ao carregar filiais'));

    listarAreas({ ativo: 'true' }).then(res => {
      if (res.success) setAreas(res.areas);
    }).catch((err: any) => setErro(err?.message || 'Erro ao carregar áreas'));

    buscarConfiguracoesConferencia().then(res => {
      if (res.success) {
        setExibirDoca(res.configuracao.exibir_doca !== false);
      }
    }).catch(() => {});
  }, [carregarAgendamentos]);

  // Recarregar áreas quando o cliente muda (filtrar pelas áreas atendidas)
  useEffect(() => {
    const params: { ativo: string; cliente_id?: string | number } = { ativo: 'true' };
    if (formData.cliente_id) {
      params.cliente_id = formData.cliente_id;
    }
    listarAreas(params).then(res => {
      if (res.success) {
        setAreas(res.areas);
      }
    }).catch(() => {});
  }, [formData.cliente_id]);

  // Filter areas by selected branch
  const areasFiltradas = formData.filial_id
    ? areas.filter(a => !a.filial_id || a.filial_id === Number(formData.filial_id))
    : areas;

  // Areas already added
  const areasJaAdicionadas = new Set(areasSelecionadas.map(a => a.area_entrega_id));

  // Available areas (not yet added)
  const areasDisponiveis = areasFiltradas.filter(a => !areasJaAdicionadas.has(a.id));

  // =========================================
  // Single add mode
  // =========================================
  const adicionarAreaModelo = () => {
    if (!novaArea.area_entrega_id) return;
    const areaId = Number(novaArea.area_entrega_id);
    if (areasJaAdicionadas.has(areaId)) {
      setErro('Esta área já foi adicionada.');
      return;
    }
    setAreasSelecionadas(prev => [...prev, {
      area_entrega_id: areaId,
      doca: novaArea.doca,
      tipo_palete: novaArea.tipo_palete
    }]);
    setNovaArea({ area_entrega_id: '', doca: '', tipo_palete: 'normal' });
    setErro('');
  };

  // =========================================
  // Multi-select mode
  // =========================================
  const toggleAreaCheckbox = (areaId: number) => {
    setAreasCheckbox(prev =>
      prev.includes(areaId) ? prev.filter(id => id !== areaId) : [...prev, areaId]
    );
  };

  const selecionarTodasAreas = () => {
    if (areasCheckbox.length === areasDisponiveis.length) {
      setAreasCheckbox([]);
    } else {
      setAreasCheckbox(areasDisponiveis.map(a => a.id));
    }
  };

  const adicionarAreasMultiplas = () => {
    if (areasCheckbox.length === 0) return;
    const novas = areasCheckbox
      .filter(id => !areasJaAdicionadas.has(id))
      .map(areaId => ({
        area_entrega_id: areaId,
        doca: docaPadrao,
        tipo_palete: tipoPaletePadrao
      }));
    setAreasSelecionadas(prev => [...prev, ...novas]);
    setAreasCheckbox([]);
    setDocaPadrao('');
    setTipoPaletePadrao('normal');
    setModoSelecaoMultipla(false);
    setErro('');
  };

  const removerAreaModelo = (index: number) => {
    setAreasSelecionadas(prev => prev.filter((_, i) => i !== index));
  };

  const getAreaNome = (areaId: number) => {
    const area = areas.find(a => a.id === areaId);
    return area ? area.nome : `Area #${areaId}`;
  };

  const getAreaRegiao = (areaId: number) => {
    const area = areas.find(a => a.id === areaId);
    return area?.regiao || '';
  };

  const getAreaFilialNome = (areaId: number) => {
    const area = areas.find(a => a.id === areaId);
    if (!area?.filial_id) return null;
    const filial = filiais.find(f => f.id === area.filial_id);
    return filial ? (filial.nome_fantasia || filial.razao_social) : null;
  };

  const resetForm = () => {
    setShowForm(false);
    setEditandoId(null);
    setErro('');
    setFormData({ cliente_id: '', filial_id: '', local_pre_carregamento: 'cliente', intervalo_dias: 1, horario: '08:00', dias_semana: [] });
    setAreasSelecionadas([]);
    setAreasCheckbox([]);
    setDocaPadrao('');
    setModoSelecaoMultipla(false);
    setEnviosAutomaticos([]);
  };

  const handleEditar = async (agId: number) => {
    try {
      setErro('');
      const res = await buscarAgendamento(agId);
      if (res.success && res.agendamento) {
        const ag = res.agendamento;
        setFormData({
          cliente_id: ag.cliente_id || '',
          filial_id: ag.filial_id || '',
          local_pre_carregamento: ag.local_pre_carregamento || 'base_transportadora',
          intervalo_dias: ag.intervalo_dias,
          horario: typeof ag.horario === 'string' ? ag.horario.substring(0, 5) : ag.horario,
          dias_semana: ag.dias_semana || []
        });
        setAreasSelecionadas(
          ag.areas?.map(a => ({
            area_entrega_id: a.area_entrega_id,
            doca: a.doca || '',
            tipo_palete: (a.tipo_palete || 'normal') as TipoPalete
          })) || []
        );
        setEditandoId(agId);
        setShowForm(true);

        // Carregar envios automáticos
        try {
          const enviosRes = await listarEnviosAutomaticos(agId);
          setEnviosAutomaticos((enviosRes as any).data || []);
        } catch (e) {
          console.error('Erro ao carregar envios automáticos:', e);
          setErro('Erro ao carregar configurações de envio automático');
        }
      }
    } catch (error: any) {
      setErro(error?.message || 'Erro ao carregar agendamento para edição');
    }
  };

  const handleSalvar = async () => {
    if (!formData.horario) {
      setErro('Horário é obrigatório');
      return;
    }
    if (formData.dias_semana.length === 0 && !formData.intervalo_dias) {
      setErro('Selecione os dias da semana ou defina um intervalo em dias');
      return;
    }
    if (areasSelecionadas.length === 0) {
      setErro('Selecione pelo menos uma área de entrega');
      return;
    }
    setErro('');

    const payload = {
      cliente_id: formData.cliente_id ? Number(formData.cliente_id) : undefined,
      filial_id: formData.filial_id ? Number(formData.filial_id) : undefined,
      local_pre_carregamento: formData.local_pre_carregamento,
      intervalo_dias: formData.intervalo_dias,
      horario: formData.horario,
      dias_semana: formData.dias_semana.length > 0 ? formData.dias_semana : null,
      areas: areasSelecionadas.length > 0 ? areasSelecionadas : undefined
    };

    try {
      let agId = editandoId;
      if (editandoId) {
        await atualizarAgendamento(editandoId, payload);
      } else {
        const res = await criarAgendamento(payload);
        agId = (res as any).agendamento?.id || (res as any).data?.id;
      }

      // Salvar envios automáticos (tanto na criação quanto na edição)
      if (agId) {
        try {
          await salvarEnviosAgendamento(agId, enviosAutomaticos);
        } catch (envioErr) {
          console.error('Erro ao salvar envios automáticos:', envioErr);
          setErro('Agendamento salvo, mas houve erro ao salvar envios automáticos');
        }
      }

      resetForm();
      carregarAgendamentos();
    } catch (error: any) {
      setErro(error?.message || 'Erro ao salvar agendamento');
    }
  };

  const handleToggleAtivo = async (ag: MontagemAgendamento) => {
    try {
      await atualizarAgendamento(ag.id, { ativo: !ag.ativo });
      carregarAgendamentos();
    } catch (error: any) {
      setErro(error?.message || 'Erro ao atualizar agendamento');
    }
  };

  const handleDeletar = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este agendamento?')) return;
    try {
      await deletarAgendamento(id);
      carregarAgendamentos();
    } catch (error: any) {
      setErro(error?.message || 'Erro ao excluir agendamento');
    }
  };

  return (
    <div className="page-container">
      <button className="montagem-back-link" onClick={() => navigate('/montar-carga')}>
        <i className="fas fa-arrow-left"></i> Voltar
      </button>

      <div className="page-header">
        <h1>
          <i className="fas fa-calendar-alt" style={{ marginRight: 8 }}></i>
          Agendamentos Automáticos
        </h1>
        <button className="btn btn-primary" onClick={() => showForm ? resetForm() : setShowForm(true)}>
          <i className={`fas ${showForm ? 'fa-minus' : 'fa-plus'}`}></i>
          {showForm ? 'Fechar' : 'Novo Agendamento'}
        </button>
      </div>

      {/* Formulario de Criacao */}
      {showForm && (
        <div className="card" style={{ marginBottom: 24 }}>
          <div className="card-header">
            <h3 style={{ margin: 0, fontSize: '1rem' }}>
              <i className={`fas ${editandoId ? 'fa-edit' : 'fa-clock'}`} style={{ marginRight: 8, color: editandoId ? '#3498db' : '#f39c12' }}></i>
              {editandoId ? `Editar Agendamento #${editandoId}` : 'Novo Agendamento Automático'}
            </h3>
          </div>
          <div className="card-body">
            <p style={{ fontSize: '0.85rem', color: '#7f8c8d', marginBottom: 16 }}>
              Configure a criação automática de operações de montagem. O sistema criará uma nova operação
              automaticamente conforme os dias da semana (ou intervalo em dias) e horário definidos.
            </p>

            <div className="montagem-form-grid">
              <div className="form-group">
                <label>Cliente</label>
                <select className="form-control" value={formData.cliente_id} onChange={(e) => handleClienteChange(e.target.value)}>
                  <option value="">Selecione um cliente</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.razao_social}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Filial</label>
                <select className="form-control" value={formData.filial_id} onChange={(e) => { setFormData(p => ({ ...p, filial_id: e.target.value })); setAreasCheckbox([]); }}>
                  <option value="">Todas as filiais</option>
                  {filiais.map(f => <option key={f.id} value={f.id}>{f.nome_fantasia || f.razao_social}</option>)}
                </select>
              </div>

              <div className="form-group">
                <label>Local do Pré-Carregamento</label>
                <select className="form-control" value={formData.local_pre_carregamento} onChange={(e) => setFormData(p => ({ ...p, local_pre_carregamento: e.target.value as LocalPreCarregamento }))}>
                  <option value="cliente">No Cliente</option>
                  <option value="base_transportadora">Base da Transportadora</option>
                </select>
              </div>

              <div className="form-group">
                <label style={{ color: formData.dias_semana.length > 0 ? '#b0b0b0' : undefined }}>
                  Intervalo (dias) {formData.dias_semana.length === 0 ? '*' : ''}
                </label>
                <input
                  type="number"
                  className="form-control"
                  min={1}
                  value={formData.intervalo_dias}
                  onChange={(e) => setFormData(p => ({ ...p, intervalo_dias: parseInt(e.target.value) || 1 }))}
                  placeholder="A cada quantos dias"
                  disabled={formData.dias_semana.length > 0}
                  style={{ opacity: formData.dias_semana.length > 0 ? 0.5 : 1 }}
                />
              </div>

              <div className="form-group">
                <label>Horário *</label>
                <input
                  type="time"
                  className="form-control"
                  value={formData.horario}
                  onChange={(e) => setFormData(p => ({ ...p, horario: e.target.value }))}
                />
              </div>
            </div>

            {/* Dias da Semana */}
            <div style={{ marginTop: 16, marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 8, fontSize: '0.9rem' }}>
                <i className="fas fa-calendar-week" style={{ marginRight: 6, color: '#8e44ad' }}></i>
                Dias da Semana
                <span style={{ fontWeight: 400, color: '#7f8c8d', fontSize: '0.8rem', marginLeft: 8 }}>
                  (deixe vazio para usar o intervalo em dias)
                </span>
              </label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {DIAS_SEMANA_LABELS.map(dia => (
                  <button
                    key={dia.value}
                    type="button"
                    title={dia.full}
                    onClick={() => toggleDiaSemana(dia.value)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: 8,
                      border: `2px solid ${formData.dias_semana.includes(dia.value) ? '#8e44ad' : '#dce1e6'}`,
                      background: formData.dias_semana.includes(dia.value) ? '#f4ecf7' : 'white',
                      color: formData.dias_semana.includes(dia.value) ? '#8e44ad' : '#2c3e50',
                      fontWeight: formData.dias_semana.includes(dia.value) ? 600 : 400,
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      transition: 'all 0.2s',
                      minWidth: 50,
                      textAlign: 'center' as const
                    }}
                  >
                    {dia.label}
                  </button>
                ))}
              </div>
              {formData.dias_semana.length > 0 && (
                <p style={{ fontSize: '0.8rem', color: '#8e44ad', marginTop: 6 }}>
                  <i className="fas fa-info-circle" style={{ marginRight: 4 }}></i>
                  O agendamento será executado apenas nos dias selecionados, no horário configurado.
                  O campo "Intervalo (dias)" será ignorado.
                </p>
              )}
            </div>

            {/* Areas modelo para criação automática */}
            <h4 style={{ fontSize: '0.9rem', fontWeight: 600, color: '#2c3e50', marginTop: 24, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>
                <i className="fas fa-map-marker-alt" style={{ marginRight: 6, color: '#e74c3c' }}></i>
                Areas de Entrega (modelo dos paletes)
                {areasSelecionadas.length > 0 && (
                  <span style={{ fontWeight: 400, color: '#7f8c8d', fontSize: '0.8rem', marginLeft: 8 }}>
                    ({areasSelecionadas.length} selecionada{areasSelecionadas.length !== 1 ? 's' : ''})
                  </span>
                )}
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline"
                onClick={() => setModoSelecaoMultipla(!modoSelecaoMultipla)}
                style={{ fontSize: '0.8rem' }}
              >
                <i className={`fas ${modoSelecaoMultipla ? 'fa-minus' : 'fa-th-list'}`}></i>
                {modoSelecaoMultipla ? 'Modo Individual' : 'Selecionar Várias'}
              </button>
            </h4>
            <p style={{ fontSize: '0.8rem', color: '#7f8c8d', marginBottom: 12 }}>
              Defina quais áreas de entrega e docas serão criadas automaticamente em cada operação agendada.
              {formData.filial_id && (
                <span style={{ display: 'block', marginTop: 4, color: '#3498db' }}>
                  <i className="fas fa-filter" style={{ marginRight: 4 }}></i>
                  Mostrando apenas areas da filial selecionada
                </span>
              )}
            </p>

            {/* Multi-select mode */}
            {modoSelecaoMultipla ? (
              <div className="palete-form-inline" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                    Selecione as areas ({areasCheckbox.length} selecionada{areasCheckbox.length !== 1 ? 's' : ''})
                  </label>
                  <button type="button" className="btn btn-sm btn-outline" onClick={selecionarTodasAreas} style={{ fontSize: '0.75rem' }}>
                    {areasCheckbox.length === areasDisponiveis.length ? 'Desmarcar Todas' : 'Selecionar Todas'}
                  </button>
                </div>

                {areasDisponiveis.length === 0 ? (
                  <p style={{ fontSize: '0.85rem', color: '#7f8c8d', fontStyle: 'italic', padding: '12px 0' }}>
                    Todas as áreas {formData.filial_id ? 'desta filial ' : ''}já foram adicionadas.
                  </p>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: 250, overflowY: 'auto', padding: 4 }}>
                    {areasDisponiveis.map(area => (
                      <label
                        key={area.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px',
                          borderRadius: 8, border: `2px solid ${areasCheckbox.includes(area.id) ? '#3498db' : '#dce1e6'}`,
                          background: areasCheckbox.includes(area.id) ? '#ebf5fb' : 'white',
                          cursor: 'pointer', fontSize: '0.85rem', transition: 'all 0.2s'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={areasCheckbox.includes(area.id)}
                          onChange={() => toggleAreaCheckbox(area.id)}
                        />
                        <span>
                          {area.nome}
                          {area.regiao && <span style={{ color: '#7f8c8d' }}> ({area.regiao})</span>}
                          {area.filial_id && !formData.filial_id && (
                            <span style={{ color: '#95a5a6', fontSize: '0.75rem', marginLeft: 4 }}>
                              [{filiais.find(f => f.id === area.filial_id)?.nome_fantasia || ''}]
                            </span>
                          )}
                        </span>
                      </label>
                    ))}
                  </div>
                )}

                <div className="form-row" style={{ marginTop: 12 }}>
                  {exibirDoca && (
                    <div className="form-group">
                      <label>Doca (aplicar a todos)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={docaPadrao}
                        onChange={(e) => setDocaPadrao(e.target.value)}
                        placeholder="Ex: DOCA 01"
                      />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Tipo (aplicar a todos)</label>
                    <select
                      className="form-control"
                      value={tipoPaletePadrao}
                      onChange={(e) => setTipoPaletePadrao(e.target.value as TipoPalete)}
                    >
                      <option value="normal">Normal</option>
                      <option value="agendamento">Agendamento</option>
                    </select>
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-primary" onClick={adicionarAreasMultiplas} disabled={areasCheckbox.length === 0}>
                    <i className="fas fa-plus"></i> Adicionar {areasCheckbox.length} Area{areasCheckbox.length !== 1 ? 's' : ''}
                  </button>
                </div>
              </div>
            ) : (
              /* Single add mode */
              <div className="palete-form-inline" style={{ marginBottom: 12 }}>
                <div className="form-row">
                  <div className="form-group">
                    <label>Área de Entrega</label>
                    <select className="form-control" value={novaArea.area_entrega_id} onChange={(e) => setNovaArea(p => ({ ...p, area_entrega_id: e.target.value }))}>
                      <option value="">Selecione uma area</option>
                      {areasDisponiveis.map(a => (
                        <option key={a.id} value={a.id}>
                          {a.nome} {a.regiao ? `(${a.regiao})` : ''}
                          {a.filial_id && !formData.filial_id ? ` [${filiais.find(f => f.id === a.filial_id)?.nome_fantasia || ''}]` : ''}
                        </option>
                      ))}
                    </select>
                  </div>
                  {exibirDoca && (
                    <div className="form-group">
                      <label>Doca</label>
                      <input type="text" className="form-control" value={novaArea.doca} onChange={(e) => setNovaArea(p => ({ ...p, doca: e.target.value }))} placeholder="Ex: DOCA 01" />
                    </div>
                  )}
                  <div className="form-group">
                    <label>Tipo</label>
                    <select
                      className="form-control"
                      value={novaArea.tipo_palete}
                      onChange={(e) => setNovaArea(p => ({ ...p, tipo_palete: e.target.value as TipoPalete }))}
                    >
                      <option value="normal">Normal</option>
                      <option value="agendamento">Agendamento</option>
                    </select>
                  </div>
                </div>
                <div className="form-actions">
                  <button type="button" className="btn btn-primary btn-sm" onClick={adicionarAreaModelo} disabled={!novaArea.area_entrega_id}>
                    <i className="fas fa-plus"></i> Adicionar Área
                  </button>
                </div>
              </div>
            )}

            {/* Lista de areas selecionadas */}
            {areasSelecionadas.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {areasSelecionadas.map((a, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 12px', background: '#f8f9fa', borderRadius: 8, marginBottom: 6 }}>
                    <i className="fas fa-map-marker-alt" style={{ color: '#e74c3c' }}></i>
                    <span style={{ flex: 1, fontSize: '0.9rem' }}>
                      <strong>{getAreaNome(a.area_entrega_id)}</strong>
                      {getAreaRegiao(a.area_entrega_id) && (
                        <span style={{ color: '#7f8c8d', fontSize: '0.8rem' }}> ({getAreaRegiao(a.area_entrega_id)})</span>
                      )}
                      {a.doca && <> - Doca: {a.doca}</>}
                      {a.tipo_palete === 'agendamento' && (
                        <span style={{ marginLeft: 6, padding: '1px 6px', background: '#fde8d0', color: '#a04000', borderRadius: 4, fontSize: '0.7rem', fontWeight: 600 }}>
                          AGENDAMENTO
                        </span>
                      )}
                      {getAreaFilialNome(a.area_entrega_id) && (
                        <span style={{ color: '#95a5a6', fontSize: '0.75rem', marginLeft: 6 }}>
                          [{getAreaFilialNome(a.area_entrega_id)}]
                        </span>
                      )}
                    </span>
                    <button type="button" onClick={() => removerAreaModelo(i)} style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', padding: 4 }}>
                      <i className="fas fa-times"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Envio Automático de Documentos */}
            <EnvioAutomaticoConfig
              agendamentoId={editandoId || undefined}
              envios={enviosAutomaticos}
              onChange={setEnviosAutomaticos}
            />

            {erro && (
              <div className="montagem-error" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                <i className="fas fa-exclamation-circle"></i>
                <span>{erro}</span>
                <button onClick={() => setErro('')} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '1rem' }}>
                  <i className="fas fa-times"></i>
                </button>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
              <button type="button" className="btn btn-outline" onClick={resetForm}>Cancelar</button>
              <button type="button" className="btn btn-primary" onClick={handleSalvar}>
                <i className={`fas ${editandoId ? 'fa-save' : 'fa-plus'}`}></i> {editandoId ? 'Salvar Alterações' : 'Criar Agendamento'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lista de Agendamentos */}
      <div className="card">
        <div className="card-header">
          <h3 style={{ margin: 0, fontSize: '1rem' }}>
            <i className="fas fa-list" style={{ marginRight: 8, color: '#3498db' }}></i>
            Agendamentos Configurados ({agendamentos.length})
          </h3>
        </div>
        <div className="card-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
              <div className="loading-spinner-large"></div>
              <p>Carregando agendamentos...</p>
            </div>
          ) : agendamentos.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
              <i className="fas fa-calendar-alt" style={{ fontSize: '2.5rem', color: '#ddd', display: 'block', marginBottom: 12 }}></i>
              <p>Nenhum agendamento automático configurado</p>
              <p style={{ fontSize: '0.85rem' }}>Crie um agendamento para gerar operações automaticamente</p>
            </div>
          ) : (
            <div className="data-table-wrapper">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Cliente</th>
                    <th>Filial</th>
                    <th>Local</th>
                    <th>Recorrência</th>
                    <th>Horário</th>
                    <th>Próxima Execução</th>
                    <th>Última Execução</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'right' }}>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {agendamentos.map(ag => (
                    <tr key={ag.id}>
                      <td style={{ fontWeight: 500 }}>#{ag.id}</td>
                      <td>{ag.cliente_razao_social || '-'}</td>
                      <td>{ag.filial_nome || '-'}</td>
                      <td>
                        {ag.local_pre_carregamento === 'cliente' ? (
                          <span><i className="fas fa-building" style={{ marginRight: 4, color: '#7f8c8d' }}></i>Cliente</span>
                        ) : (
                          <span><i className="fas fa-warehouse" style={{ marginRight: 4, color: '#7f8c8d' }}></i>Base</span>
                        )}
                      </td>
                      <td>
                        {ag.dias_semana && ag.dias_semana.length > 0 ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
                            <i className="fas fa-calendar-week" style={{ color: '#8e44ad' }}></i>
                            {[1,2,3,4,5,6,0].filter(d => ag.dias_semana!.includes(d)).map(d =>
                              ['Dom','Seg','Ter','Qua','Qui','Sex','Sáb'][d]
                            ).join(', ')}
                          </span>
                        ) : (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                            <i className="fas fa-redo" style={{ color: '#3498db' }}></i>
                            A cada {ag.intervalo_dias} dia{ag.intervalo_dias > 1 ? 's' : ''}
                          </span>
                        )}
                      </td>
                      <td>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <i className="fas fa-clock" style={{ color: '#f39c12' }}></i>
                          {typeof ag.horario === 'string' ? ag.horario.substring(0, 5) : ag.horario}
                        </span>
                      </td>
                      <td>
                        {ag.proxima_execucao
                          ? new Date(ag.proxima_execucao).toLocaleDateString('pt-BR') + ' ' + new Date(ag.proxima_execucao).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
                          : '-'
                        }
                      </td>
                      <td>
                        {ag.ultima_execucao
                          ? new Date(ag.ultima_execucao).toLocaleDateString('pt-BR') + ' ' + new Date(ag.ultima_execucao).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
                          : 'Nunca'
                        }
                      </td>
                      <td>
                        <span
                          className={`status-badge ${ag.ativo ? 'status-montagem-pendente' : 'status-montagem-cancelada'}`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleToggleAtivo(ag)}
                          title="Clique para alternar"
                        >
                          {ag.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <div className="action-buttons" style={{ justifyContent: 'flex-end' }}>
                          <button className="btn-edit" onClick={() => handleEditar(ag.id)} title="Editar">
                            <i className="fas fa-edit"></i>
                          </button>
                          <button className="btn-edit" onClick={() => handleToggleAtivo(ag)} title={ag.ativo ? 'Desativar' : 'Ativar'}>
                            <i className={`fas ${ag.ativo ? 'fa-pause' : 'fa-play'}`}></i>
                          </button>
                          <button className="btn-delete" onClick={() => handleDeletar(ag.id)} title="Excluir">
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MontagemPaleteAgendamentos;
